import type {
  NodeWrite,
  PropertyValueGroupV3,
  ViewReference,
} from '@cognite/sdk';

type NodeWriteWithViewReference = NodeWrite & {
  sources: {
    source: ViewReference;
    properties: PropertyValueGroupV3;
  }[];
};

/**
 * Creates a single node with a direct relation property.
 * Used to convert edges into nodes with direct relations instead of traditional edge representations.
 *
 * @param parentExternalId - The external ID of the parent node (becomes the direct relation value)
 * @param childExternalId - The external ID of the child node (becomes the node's externalId)
 * @param viewReference - The view reference for the node's source
 * @param viewProperty - The property name for the direct relation (e.g., 'checklist', 'template', 'item')
 * @param instanceSpace - The space where the node and relation exist
 * @returns A NodeWrite object ready for `instances.upsert`
 */
export const createChildToParentDirectRelationship = (
  parentExternalId: string,
  childExternalId: string,
  viewReference: ViewReference,
  viewProperty: string,
  instanceSpace: string
): NodeWriteWithViewReference => {
  return {
    instanceType: 'node' as const,
    space: instanceSpace,
    externalId: childExternalId,
    sources: [
      {
        source: {
          space: viewReference.space,
          externalId: viewReference.externalId,
          version: viewReference.version,
          type: 'view' as const,
        },
        properties: {
          [viewProperty]: {
            space: instanceSpace,
            externalId: parentExternalId,
          },
        },
      },
    ],
  };
};
